import { Adunit } from '@/components/Adunit';
import { RecommendedArticles } from '@/components/RecommendedArticles';
import { useAppState } from '@/hooks/useAppState';
import { useClassName } from '@/styles/Article';
import { Device } from '@/types/app';
import { Component, ComponentProps } from '@/types/component';
import { getUrl } from '@/utils/getUrl';
import { http } from '@/utils/http';
import { mergeProps } from '@/utils/merge';
import { getOcelotConfig } from 'lib/configs/ocelot';
import { RecommendedArticle } from 'lib/data/recommendations';
import { findFirstOfType } from 'lib/labrador/content-operations/findBy';
import { useEffect, useState } from 'react';
import { RecommendedArticlesData } from '../RecommendedArticles';

type RecommendationSectionElement = 'recommendedArticles' | 'adUnit' | 'affiliateGallery' | 'moreRecommendedArticles';

const RECOMMENDATION_SECTION_ELEMENTS_FALLBACK: RecommendationSectionElement[] = [
  'recommendedArticles',
  'adUnit',
  'affiliateGallery',
  'moreRecommendedArticles',
];

const getRecommendationSectionElements = (): RecommendationSectionElement[] => {
  // in future it will be ordered dynamically based on user preferences
  return RECOMMENDATION_SECTION_ELEMENTS_FALLBACK;
};

const getRecommendedArticles = async (recommendedArticles: RecommendedArticle[], device: Device) => {
  const responses = await Promise.all(
    recommendedArticles.map((article) => {
      const url = getUrl('/api/page')!;
      const params = new URLSearchParams({
        route: `article/${article.article_id}`,
        device,
      });
      url.search = params.toString();

      return http.get(url.href);
    }),
  );

  const articles = responses.reduce<RecommendedArticlesData>((acc, response) => {
    if (!response?.data) return acc;

    const { title, subtitle, publishedUrl, category } = response?.data?.data ?? {};
    const { id } = response?.data?.meta ?? {};
    const matchedArticle = recommendedArticles.find((article) => article.article_id === id.toString());
    const articleHeader = findFirstOfType(response?.data?.children ?? [], 'articleHeader');
    const articleImage = findFirstOfType(articleHeader?.children ?? [], 'labradorImage');
    const imageSrc = articleImage?.data?.imageUrl?.[device];
    const imageAlt = articleImage?.data?.alt;

    return [
      ...acc,
      {
        headline: title,
        description: subtitle,
        caption: category,
        image: {
          src: imageSrc,
          alt: imageAlt,
        },
        links: {
          article: { href: publishedUrl },
        },
        model: matchedArticle?.model ?? '',
        type: matchedArticle?.type ?? '',
      },
    ];
  }, []);

  return articles;
};

export interface ArticleRecommendedProps extends ComponentProps<'div'> {
  affiliateGallery?: JSX.Element;
}

export const ArticleRecommendedComponent: Component<ArticleRecommendedProps> = ({ affiliateGallery, ...props }) => {
  const [{ recommendedArticles, device }] = useAppState();
  const [recommendedArticleTeasers, setRecommendedArticleTeasers] = useState<RecommendedArticlesData | undefined>(
    undefined,
  );

  const elements = getRecommendationSectionElements();

  useEffect(() => {
    if (recommendedArticleTeasers?.length || !getOcelotConfig('features.recommendedArticles.enabled')) return;

    getRecommendedArticles(recommendedArticles, device).then((articles) => {
      setRecommendedArticleTeasers(articles);
    });
  }, [recommendedArticles]);

  const components = elements
    .map((elementType, idx) => {
      switch (elementType) {
        case 'recommendedArticles':
          return (
            <RecommendedArticles
              key={idx}
              isLoading={!recommendedArticleTeasers}
              articles={recommendedArticleTeasers}
            />
          );
        case 'adUnit':
          return <Adunit data={{ placementType: 'front_split', adsUniqueId: '2' }} key={idx} />;
        case 'affiliateGallery':
          return affiliateGallery && { ...affiliateGallery, key: idx };
        case 'moreRecommendedArticles':
          return <div key={idx} id="more-recommended-articles-portal" className="empty:hidden" />;
        default:
          return undefined;
      }
    })
    .filter(Boolean) as JSX.Element[];

  const className = useClassName('recommended', props);

  return components.length > 0 ? <div {...mergeProps({ className }, props)}>{components}</div> : <></>;
};
